import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Paper,
  Grid,
  Autocomplete,
  TextField,
  Button,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  IconButton,
  Divider,
  Stack,
  Checkbox,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, Print as PrintIcon } from '@mui/icons-material';
import { stockExitsAPI } from '../services/api';
import { StockExitNew, StockExitPayment } from '../types/stockExitNew';
import useAlert from '../hooks/useAlert';
import usePdfPreview from '../hooks/usePdfPreview';
import AlertDialog from './AlertDialog';
import PriceInput, { parsePriceValue } from './PriceInput';
import { extractErrorMessage } from '../utils/errorHandler';

const STOCK_COLOR = '#2196F3';

const fmt = (v: any): string =>
  `${Number(v || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} MAD`;

const today = () => new Date().toISOString().split('T')[0];

interface ExitOption {
  id: number;
  bon_number: string;
  date: string;
  client_id: number | null;
  client_name?: string | null;
}

const StockExitPaymentsTab: React.FC = () => {
  const [exits, setExits] = useState<ExitOption[]>([]);
  const [selectedOption, setSelectedOption] = useState<ExitOption | null>(null);
  const [selectedExit, setSelectedExit] = useState<StockExitNew | null>(null);
  const [payments, setPayments] = useState<StockExitPayment[]>([]);
  const [amount, setAmount] = useState<number | null | string>('');
  const [date, setDate] = useState<string>(today());
  const [saving, setSaving] = useState(false);
  const [selectedPayments, setSelectedPayments] = useState<Set<number>>(new Set());

  const { alertState, showError, showSuccess, showConfirm, hideAlert } = useAlert();
  const { openPreview, pdfPreviewElement } = usePdfPreview();

  const allPaymentsSelected = payments.length > 0 && selectedPayments.size === payments.length;
  const somePaymentsSelected = selectedPayments.size > 0 && !allPaymentsSelected;

  const togglePayment = (id: number) => {
    setSelectedPayments((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };
  const toggleAllPayments = () => {
    setSelectedPayments(allPaymentsSelected ? new Set() : new Set(payments.map((p) => p.id)));
  };

  const handlePrint = () => {
    if (!selectedExit) return;
    const chosen = payments.filter((p) => selectedPayments.has(p.id));
    if (chosen.length === 0) return;
    openPreview({
      getBlob: async () => {
        const { generateStockExitPaymentsPDF } = await import('../utils/pdfGenerator');
        return generateStockExitPaymentsPDF(selectedExit, chosen, true) as Blob;
      },
      fileName: `Paiements_${selectedExit.bon_number}_${new Date().toISOString().split('T')[0]}.pdf`,
    });
  };

  // Load ALL exits once (no pagination) for the dropdown; filtering is client-side.
  useEffect(() => {
    stockExitsAPI
      .getAllForDropdown()
      .then(setExits)
      .catch((e) => showError(extractErrorMessage(e)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refreshSelected = async (exitId: number) => {
    const [exit, pays] = await Promise.all([
      stockExitsAPI.getById(exitId),
      stockExitsAPI.getPayments(exitId),
    ]);
    setSelectedExit(exit);
    setPayments(pays);
    setSelectedPayments(new Set());
  };

  const handleSelect = async (option: ExitOption | null) => {
    setSelectedOption(option);
    setSelectedExit(null);
    setPayments([]);
    setSelectedPayments(new Set());
    setAmount('');
    setDate(option?.date || today());
    if (option?.id) {
      try {
        await refreshSelected(option.id);
      } catch (e: any) {
        showError(extractErrorMessage(e));
      }
    }
  };

  const handleAdd = async () => {
    if (!selectedExit?.id) return;
    const value = parsePriceValue(amount);
    if (!value || value <= 0) {
      showError('Veuillez saisir un montant valide (> 0)');
      return;
    }
    setSaving(true);
    try {
      await stockExitsAPI.addPayment(selectedExit.id, { amount: value, date: date || today() });
      showSuccess('Paiement ajouté');
      setAmount('');
      await refreshSelected(selectedExit.id);
    } catch (e: any) {
      showError(extractErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (paymentId: number) => {
    showConfirm('Supprimer ce paiement ?', async () => {
      try {
        await stockExitsAPI.deletePayment(paymentId);
        showSuccess('Paiement supprimé');
        if (selectedExit?.id) await refreshSelected(selectedExit.id);
      } catch (e: any) {
        showError(extractErrorMessage(e));
      }
    });
  };

  const remainingColor = useMemo(
    () => (Number(selectedExit?.remaining || 0) > 0 ? '#f44336' : '#4caf50'),
    [selectedExit]
  );

  return (
    <Box>
      <Paper sx={{ p: 2, mb: 2 }}>
        <Autocomplete
          options={exits}
          value={selectedOption}
          onChange={(_e, v) => handleSelect(v)}
          getOptionLabel={(o) =>
            `${o.bon_number}${o.client_name ? ` — ${o.client_name}` : ''}${
              o.date ? ` (${new Date(o.date).toLocaleDateString('fr-FR')})` : ''
            }`
          }
          isOptionEqualToValue={(a, b) => a.id === b.id}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Rechercher une sortie de stock (BON N°)"
              variant="filled"
              placeholder="Tapez un numéro de BON…"
            />
          )}
        />
      </Paper>

      {selectedExit && (
        <>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            {[
              { label: 'Total', value: selectedExit.grand_total, color: '#0d47a1' },
              { label: 'Avance (création)', value: selectedExit.advance, color: '#6a1b9a' },
              { label: 'Paiements ultérieurs', value: selectedExit.total_payments, color: '#1565c0' },
              { label: 'Total payé', value: selectedExit.total_paid, color: '#2e7d32' },
              { label: 'Reste à payer', value: selectedExit.remaining, color: remainingColor },
            ].map((c) => (
              <Grid item xs={12} sm={6} md key={c.label}>
                <Paper sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="caption" color="text.secondary">
                    {c.label}
                  </Typography>
                  <Typography variant="h6" sx={{ color: c.color, fontWeight: 700 }}>
                    {fmt(c.value)}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>

          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: STOCK_COLOR, mb: 1 }}>
              Ajouter un paiement
            </Typography>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={4}>
                <PriceInput
                  label="Montant"
                  variant="filled"
                  size="medium"
                  value={amount}
                  onChange={(v) => setAmount(v)}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Date"
                  type="date"
                  variant="filled"
                  fullWidth
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleAdd}
                  disabled={saving}
                  sx={{ backgroundColor: STOCK_COLOR, '&:hover': { backgroundColor: '#1976D2' } }}
                >
                  Ajouter
                </Button>
              </Grid>
            </Grid>
          </Paper>

          <Divider sx={{ mb: 1 }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Paiements enregistrés
            </Typography>
            <Button
              variant="contained"
              startIcon={<PrintIcon />}
              onClick={handlePrint}
              disabled={selectedPayments.size === 0}
              sx={{ backgroundColor: STOCK_COLOR, '&:hover': { backgroundColor: '#1976D2' } }}
            >
              Imprimer la sélection ({selectedPayments.size})
            </Button>
          </Box>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={allPaymentsSelected}
                      indeterminate={somePaymentsSelected}
                      onChange={toggleAllPayments}
                      disabled={payments.length === 0}
                    />
                  </TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell align="right">Montant</TableCell>
                  <TableCell align="center">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {payments.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      Aucun paiement enregistré
                    </TableCell>
                  </TableRow>
                ) : (
                  payments.map((p) => (
                    <TableRow key={p.id} selected={selectedPayments.has(p.id)}>
                      <TableCell padding="checkbox">
                        <Checkbox checked={selectedPayments.has(p.id)} onChange={() => togglePayment(p.id)} />
                      </TableCell>
                      <TableCell>{new Date(p.date).toLocaleDateString('fr-FR')}</TableCell>
                      <TableCell align="right">{fmt(p.amount)}</TableCell>
                      <TableCell align="center">
                        <IconButton size="small" color="error" onClick={() => handleDelete(p.id)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {!selectedExit && (
        <Stack alignItems="center" sx={{ py: 6 }}>
          <Typography color="text.secondary">
            Sélectionnez une sortie de stock pour gérer ses paiements.
          </Typography>
        </Stack>
      )}

      <AlertDialog
        open={alertState.open}
        type={alertState.type}
        title={alertState.title}
        message={alertState.message}
        onClose={hideAlert}
        onConfirm={alertState.onConfirm}
        showCancel={alertState.showCancel}
      />
      {pdfPreviewElement}
    </Box>
  );
};

export default StockExitPaymentsTab;
